import React, { useState, useEffect, useRef } from 'react';
import { Heart } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useGalleryStore } from '../../stores/galleryStore';
import ImageWithFallback from '../common/ImageWithFallback';
import type { Piece } from '@shared/types/firestore';

const THUMBNAIL_ROOT_MARGIN = '480px 0px';
const PROMOTED_IMAGE_DELAY_MS = 250;

interface PieceThumbnailProps {
  piece: Piece;
  onClick: () => void;
  className?: string;
  showTitle?: boolean;
}

type PieceWithGalleryVariants = Piece & {
  gallery_thumbnail_path?: string | null;
  gallery_preview_path?: string | null;
};

function getGalleryTilePaths(piece: Piece): { initialPaths: string[]; promotedPaths: string[]; promotedPath?: string } {
  const pieceWithVariants = piece as PieceWithGalleryVariants;
  const initialPaths = [
    pieceWithVariants.gallery_thumbnail_path,
    piece.image_full_path,
  ].filter((value): value is string => Boolean(value));
  const promotedPath = pieceWithVariants.gallery_preview_path || piece.image_full_path || undefined;
  const promotedPaths = [
    promotedPath,
    ...initialPaths,
  ].filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index);

  return {
    initialPaths,
    promotedPaths,
    promotedPath: promotedPath && promotedPath !== initialPaths[0] ? promotedPath : undefined,
  };
}

export default function PieceThumbnail({ 
  piece, 
  onClick, 
  className = '', 
  showTitle = false 
}: PieceThumbnailProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { user } = useAuthStore();
  const isFavorited = useGalleryStore(state =>
    piece.id ? state.favoritedPieceIds.has(piece.id) : false
  );
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [shouldLoadMedia, setShouldLoadMedia] = useState(false);
  const [promoteImage, setPromoteImage] = useState(false);
  const hideTitles = user?.hideTitles === true;
  const accessibleTitle = hideTitles ? 'Artwork thumbnail' : piece.title;
  const { initialPaths, promotedPaths, promotedPath } = getGalleryTilePaths(piece);
  const tilePathKey = [...initialPaths, promotedPath ?? ''].join('|');

  useEffect(() => {
    setIsNearViewport(false);
    setShouldLoadMedia(false);
    setPromoteImage(false);

    const button = buttonRef.current;
    if (!button || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      setShouldLoadMedia(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isNear = entry.isIntersecting;
        setIsNearViewport(isNear);
        if (isNear) setShouldLoadMedia(true);
      },
      { rootMargin: THUMBNAIL_ROOT_MARGIN }
    );

    observer.observe(button);
    return () => observer.disconnect();
  }, [piece.id, tilePathKey]);

  useEffect(() => {
    if (!promotedPath || !isNearViewport || !shouldLoadMedia) return;

    const timer = window.setTimeout(() => setPromoteImage(true), PROMOTED_IMAGE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isNearViewport, piece.id, promotedPath, shouldLoadMedia]);

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      className="relative group hover:opacity-80 hover:scale-105 transition-all duration-200 w-full h-full"
      title={hideTitles ? undefined : piece.title}
      aria-label={accessibleTitle}
    >
      {/* Image Container - maintains dimensions during loading */}
      <div className={`relative ${className}`}>
        {shouldLoadMedia ? (
          <ImageWithFallback
            path={promoteImage && promotedPath ? promotedPaths : initialPaths}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="h-full w-full bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-[pulse_2.8s_ease-in-out_infinite] motion-reduce:animate-none"
            aria-hidden="true"
          />
        )}
      </div>
      
      {/* Favorite Heart Overlay */}
      {isFavorited && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-red-500 text-white p-1 rounded-full shadow-md">
            <Heart className="w-3 h-3 fill-current" />
          </div>
        </div>
      )}
      
      {/* Title Overlay (for list view) */}
      {showTitle && !hideTitles && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
          <p className="text-white text-xs font-medium truncate">
            {piece.title}
          </p>
          {piece.date && (
            <p className="text-white/80 text-xs mt-0.5">
              {piece.date}
            </p>
          )}
        </div>
      )}
      
      {/* Hover overlay for better interaction feedback */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 pointer-events-none" />
    </button>
  );
}
